/** 五个转化 / cohort 看板的数据说明；与各读服务和 adapter 的实际分组、分母及成熟度保持一致。 */
export type MetricsDataExplanationId =
  | 'activation-conversion'
  | 'paywall-conversion'
  | 'web-funnels'
  | 'first-payment-cohorts'
  | 'retention-cohorts';

type MetricsDataExplanationSection = {
  title: string;
  details: readonly string[];
  formulas?: readonly string[];
  example?: string;
};

type MetricsDataExplanationContent = {
  title: string;
  summary: string;
  sections: readonly MetricsDataExplanationSection[];
};

export const METRICS_DATA_EXPLANATIONS: Record<
  MetricsDataExplanationId,
  MetricsDataExplanationContent
> = {
  'activation-conversion': {
    title: '激活转化看板',
    summary: '观察同一批新激活用户，随后有多少人阅读、进入付费流程并完成到账。',
    sections: [
      {
        title: '统计对象与观察时间',
        details: [
          '激活取用户在该 App 的首次归因安装，包含自然量；优先取安装时间，无安装时间时取归因绑定时间。游客与登录身份归一后，按 App × 用户去重。只有首次激活落在所选日期范围内的用户进入本批统计。',
          '日期按 UTC 自然日。每位用户的行为从自己的激活时刻开始，观察至所选结束日的次日 00:00 UTC；结束日在未来或当天时，最多观察到当前时刻。',
        ],
      },
      {
        title: '人数、比例与转化率',
        details: [
          '进广告小说：进入投放落点书籍阅读；进付费章：进入任意书籍的付费章，包含已解锁或畅读放行；进面板：付费墙成功展示商品；创建订单：点击购买或 Web 发起支付；付费：Production 正式到账。人数均统计本批激活用户是否发生过该行为。',
          '各阶段独立统计是否发生，不强制每位用户逐级经过所有阶段。例如自然量用户可以直接进入付费章，部分级间转化率可能超过 100%。测试订单不计入正式到账。',
        ],
        formulas: [
          '某阶段用户比例 = 该阶段用户数 ÷ 激活人数 × 100%',
          '级间转化率 = 本级用户数 ÷ 上一级用户数 × 100%',
          '本行某指标占比 = 本行该指标人数 ÷ 汇总行该指标人数 × 100%',
        ],
        example:
          '某日激活 1,000 人，进入付费面板 200 人、点击购买 80 人、正式到账 40 人：进面板比例 = 200 ÷ 1,000 = 20%；下单转化率 = 80 ÷ 200 = 40%；付费转化率 = 40 ÷ 80 = 50%；付费用户比例 = 40 ÷ 1,000 = 4%。',
      },
      {
        title: '分组、汇总与空值',
        details: [
          '分组按本批激活用户的维度计算。汇总覆盖当前筛选的整个激活批次；激活人数门槛只隐藏分组明细，不改变汇总。汇总比例由汇总人数相除，不平均各行百分比。',
          '分母为 0 时显示「—」。行为指标仅覆盖实际采集到的埋点，用户未同意分析采集时可能没有行为记录；没有行为记录不等同于已证实没有使用。',
        ],
      },
    ],
  },
  'paywall-conversion': {
    title: '付费转化看板',
    summary: '观察所选时间窗内的活跃、展示付费面板、点击购买和正式到账人数。',
    sections: [
      {
        title: '统计对象与数据来源',
        details: [
          '活跃用户：有任意埋点事件的用户；进付费面板用户：有商品展示事件的用户；创建付费订单用户：有点击购买事件的用户；付费用户：有 Production 正式到账的用户，覆盖 IAP 与 Web 支付。游客与登录身份归一，排除测试到账。',
          '行为按事件客户端时间归 UTC 日，到账按结算时间归 UTC 日。各阶段分别统计当前时间窗内的去重用户，不要求属于新激活用户，也不要求同一个人按顺序走完所有阶段。',
        ],
      },
      {
        title: '比例、转化率与支付耗时',
        details: [
          '完成付费平均耗时取应用内上报且确认到账的支付耗时样本的平均值；未上报耗时的到账不补造耗时。ARPU、ARPPU 和付费金额占比当前未提供就绪数值，显示占位。',
        ],
        formulas: [
          '进面板 / 下单 / 付费用户比例 = 对应阶段人数 ÷ 活跃人数 × 100%',
          '下单转化率 = 点击购买人数 ÷ 进付费面板人数 × 100%',
          '付费转化率 = 正式到账人数 ÷ 点击购买人数 × 100%',
          '本行人数占比 = 本行该阶段人数 ÷ 汇总行该阶段人数 × 100%',
        ],
        example:
          '当前筛选内活跃 500 人、进面板 100 人、点击购买 40 人、到账 20 人：进面板比例 20%，下单转化率 40%，付费转化率 50%，付费用户比例 4%。两个有效支付耗时样本分别为 10 秒和 20 秒，平均耗时为 15 秒。',
      },
      {
        title: '分组与不可计算的格子',
        details: [
          '商城类型、支付方式属于阶段属性：活跃用户没有这些属性，进面板阶段没有支付方式。因此按商城类型或支付方式拆分时，缺少归因依据的列及其派生比例显示「—」；筛选只约束具有该属性的阶段。',
          '全窗口汇总重新去重，不直接累加每日去重人数；同一个用户跨天可能在多行出现。活跃人数门槛仅影响明细行。分母为 0、不可归因或尚未就绪时显示「—」，不填成 0%。',
        ],
      },
    ],
  },
  'web-funnels': {
    title: 'Web 行为漏斗',
    summary: '对单个 Web App 的有序行为链路，按每张表标明的计数单位和成熟起点计算。',
    sections: [
      {
        title: '计数单位与有序链路',
        details: [
          '只分析当前选择的一个 Web App。行为来自 Web 第一方埋点，按客户端时间归 UTC 日，游客与登录身份归一。各表可能按用户、会话、打开次数、用户 × 章节或用户 × 商品计数，按表头单位去重，不跨不同计数单位计算转化率。',
          '有序链路中，本级证据必须不早于上一级；同一事件可以证明多个阶段。不适用的阶段跳过，其下一级接上一适用级。同会话阶段要求相同 session_id。',
        ],
      },
      {
        title: '成熟起点与比例',
        details: [
          '多数转化链路观察起点日 S 至 S+6，共 7 个 UTC 自然日。第一级显示全部起点，后续阶段只统计观察完整的成熟起点；因此后续阶段的分母可能小于表里的起点总数。打开段不要求这段观察窗，新访客 D1 / D7 留存分别按目标日是否完整判断。',
          '步骤率只在本级与上一适用级使用同一批起点时显示。已解锁、畅读或限免等进入即放行的收费章，中间付费阶段不适用。',
        ],
        formulas: [
          '到达率 = 本级计数 ÷ 本级适用起点数 × 100%',
          '步骤转化率 = 本级计数 ÷ 上一适用级计数 × 100%（同一批起点）',
        ],
        example:
          '有 120 个起点，其中 80 个已成熟；这 80 个中，60 个读到正文，20 个随后到达收费章。读到正文的到达率 = 60 ÷ 80 = 75%；收费章到达率 = 20 ÷ 80 = 25%；收费章步骤转化率 = 20 ÷ 60 ≈ 33.3%。',
      },
      {
        title: '活跃、留存与到账核对',
        details: [
          '每日活跃按 web_page_view 的用户去重；新访客留存以首次 Web 访问归因人群为起点，D1 / D7 只看目标日当天是否回访。',
          '到账核对逐日比较客户端观察到账事件与 Web Production 服务端结算：事件按 event_id 去重、客户端时间归日，结算按 settled_at 归日。两者时间来源不同，单日笔数可能存在差异。',
        ],
        formulas: [
          '漏报差额 = 结算到账单数 − 观察到账单数（保留正负值）',
          '漏报率 = 漏报差额 ÷ 结算到账单数 × 100%（结算为 0 时不计算）',
        ],
        example:
          '某日服务端正式结算 10 单，客户端观察到 8 单，漏报差额为 2 单，漏报率为 20%。差额可能包含付款后没有回到观察点、换浏览器或跨日记录，不能据此认定支付失败。',
      },
      {
        title: '未就绪与观察未满',
        details: [
          '覆盖从首条符合条件的 Web 会话开始事件入库日的次日算起；早于可观测范围、缺少埋点或会话标识尚未接通时，显示「埋点接入中」。观察窗尚未结束时显示「观察未满」。这些状态都不代表真实 0；分母为 0 或没有适用起点时也不计算百分比。',
        ],
      },
    ],
  },
  'first-payment-cohorts': {
    title: '付费 cohort',
    summary: '按激活人群分组，看每位用户首次正式付费发生在激活后的哪一天。',
    sections: [
      {
        title: '激活批次与首次付费',
        details: [
          '所选日期筛选首次激活日，激活口径与激活转化看板一致，包含自然量；游客与登录身份归一，按 App × 用户去重。首次付费取激活时刻之后最早一笔 Production 正式到账。',
          '首付持续观察至当前时刻，不以所选激活结束日截断。因此较早激活的用户，后来首次付费仍会进入原激活批次。',
        ],
      },
      {
        title: '首付分桶与转化率',
        details: [
          '首付天数取首次付费 UTC 日期减激活 UTC 日期，分为 D0、D1、D2、D3–6、D7–13、D14+。每位付费用户只进入一个桶，各桶人数之和等于累计付费人数。',
          '桶转化率和累计转化率都以该行全部激活人数为分母；成熟人数用于标记观察是否充分，不替换转化率分母。',
        ],
        formulas: [
          '首付天数 = 首次正式到账 UTC 日期 − 激活 UTC 日期',
          '某桶转化率 = 首付落在该桶的人数 ÷ 该行激活人数 × 100%',
          '累计转化率 = 各桶首付人数之和 ÷ 该行激活人数 × 100%',
        ],
        example:
          '某批激活 100 人，D0 首付 10 人、D1 首付 5 人、D3–6 首付 3 人，其余桶为 0：对应转化率为 10%、5%、3%，累计 18%。某用户 24 日激活、27 日首次到账，UTC 日差为 3，计入 D3–6，后续再次付费不重复计入。',
      },
      {
        title: '观察未满与汇总',
        details: [
          '带「*」表示组内还有用户没过完对应观察期，当前人数和转化率仍可继续增加。D0 要过完激活当天，D3–6 要过完第 6 天；D14+ 从第 14 天起持续累积，不是固定长度的最终留存。',
          '汇总用总首付人数除总激活人数，不平均各行百分比。激活人数门槛只影响明细；分母为 0 显示「—」。语言行展开的地区明细沿用同一人群和公式。',
        ],
      },
    ],
  },
  'retention-cohorts': {
    title: '用户留存',
    summary: '观察同一批用户在起点后的指定天、周或月是否回访；回访只要求任意埋点事件。',
    sections: [
      {
        title: '全部 / 免费 / 付费的分组起点',
        details: [
          '全部：按首次激活日分组，包含付费和未付费用户；免费：同样按首次激活日分组，只保留截至统计时在该 App 从未有 Production 到账的用户。用户后来首次付费，会影响后续重算时的免费人群。',
          '付费：按用户在该 App 历史上最早一笔 Production 正式到账日分组，人数列为「首次付费用户」。该起点不要求晚于激活时间；测试到账不计。付费留存观察首付后的活跃回访，无需再次付费。',
          '用户按 App × 归一身份去重，游客及关联登录身份的事件合并。当前 App、语言和地区筛选同时约束所选人群及其回访数据。',
        ],
      },
      {
        title: '留存人数与留存率',
        details: [
          '回访指目标周期内有任意埋点事件，按用户去重；不要求阅读、解锁或再次付费。活跃日期取客户端时间与入库时间中较早者，再归 UTC 自然日，防止未来客户端时间把回访推到未来。',
          '每格分子和分母取同批聚合记录。左侧人数可实时更新，不能直接拿最新人数与旧批次回访数相除。',
        ],
        formulas: [
          'Dn 留存率 = 本批用户在第 n 天回访的人数 ÷ 同格聚合的本批用户人数 × 100%',
          '汇总留存率 = 各已就绪格回访人数之和 ÷ 对应格用户人数之和 × 100%',
        ],
        example:
          '24 日激活 100 人，其中截至统计时仍免费的有 80 人。25 日这批激活用户有 40 人回访，其中免费用户 24 人：全部 D1 = 40 ÷ 100 = 40%，免费 D1 = 24 ÷ 80 = 30%。另有 20 人在 24 日首次付费，25 日其中 12 人回访，则付费 D1 = 12 ÷ 20 = 60%；三者起点人群各自计算。',
      },
      {
        title: '天 / 周 / 月与汇总',
        details: [
          '按天：D1 是起点次日，D7 只看第 7 天当天，不累计前 7 天。按周：周一开始，W1 看起点所在自然周的下一周；按月：M1 看起点所在自然月的下一个月。目标周或月内任一天回访即计入。',
          '汇总只合并该列已就绪且有用户的格子，是按对应人数加权的结果。例如两组 D1 分别为 10/20 和 20/100，汇总为 30/120 = 25%，不直接平均 50% 与 20%。',
        ],
      },
      {
        title: '更新与空白状态',
        details: [
          '回访数据每日 02:30 UTC 聚合（台北时间 10:30），按页面展示的数据水位判断是否已观察完整。目标周期尚未完整或聚合水位未覆盖时显示虚线空格；已到期但缺少可用记录或没有用户时显示「—」；已就绪且有人、无人回访时才显示 0%。',
          '行为回访受埋点覆盖与用户分析采集同意范围影响。缺少事件不能证明用户没有使用；按地区观察时，早期 cohort 的地区会按既定保留与冻结口径处理。',
        ],
      },
    ],
  },
};
